import {
  completionProviders,
  type WorkflowCompletionChecklist,
  type WorkflowCompletionRequirement,
} from '@app/shared/workflow-commands/workflow-completion-requirements';
import type {
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';
import { sameCapabilityReference } from './workflow-capability-authoring';
import type { DetailSampleJourney } from './workflow-detail-sample-journey';
import {
  commandIdentity,
  type StepAuthoringCatalog,
} from './workflow-step-composition';

/** Supply a useful first required interaction without replacing an author's page. */
export function applyRequirementDetailDefaults(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  catalog: StepAuthoringCatalog | null,
): WorkflowDefinitionDocument {
  const matches = document.nodes.filter((node) => node.id === nodeId);
  const declaration = matches.length === 1 ? matches[0].declaration : null;
  if (
    !declaration ||
    declaration.detail.mainInteraction ||
    declaration.detail.blocks.some((block) => block.region === 'main') ||
    !catalog
  )
    return document;
  const commands = declaration.behavior.commands;
  for (const requirement of declaration.behavior.completionRequirements ?? []) {
    const form = requirement.provider === completionProviders.form;
    const decision = requirement.provider === completionProviders.decision;
    if (
      (!form && !decision) ||
      requirement.version !== 1 ||
      requirement.settingsVersion !== 1 ||
      !requirement.settings ||
      Object.keys(requirement.settings).some(
        (key) =>
          !(
            form
              ? ['formId']
              : ['sourceCommand', 'decisionKind', 'outcomeCodes']
          ).includes(key),
      ) ||
      catalog.requirementProviders?.filter(
        (provider) =>
          provider.provider === requirement.provider &&
          provider.version === requirement.version &&
          provider.settingsVersion === requirement.settingsVersion &&
          provider.settingsHash === requirement.settingsHash,
      ).length !== 1 ||
      declaration.behavior.completionRequirements?.filter(
        (item) => item.id === requirement.id,
      ).length !== 1
    )
      continue;
    const reference = requirement.settings['sourceCommand'] as
      | WorkflowCapabilityReference
      | undefined;
    const sources = commands.filter((command) =>
      form
        ? command.captureForm?.id === requirement.settings['formId']
        : !!reference?.key &&
          sameCapabilityReference(command.capability, reference),
    );
    if (sources.length !== 1) continue;
    const source = sources[0];
    const contracts = catalog.commands.filter((command) =>
      sameCapabilityReference(command.capability, source.capability),
    );
    const pins = document.capabilityReferences?.filter((reference) =>
      sameCapabilityReference(reference, source.capability),
    );
    const interactions = catalog.interactions.filter((interaction) =>
      sameCapabilityReference(interaction.command, source.capability),
    );
    if (
      contracts.length !== 1 ||
      (form &&
        (source.capability.key.provider !== 'workflow' ||
          source.capability.key.code !== 'workflow.form.save' ||
          source.capability.key.version !== 1 ||
          contracts[0].supportsCaptureForm !== true)) ||
      contracts[0].completesStep !== false ||
      pins?.length !== 1 ||
      interactions.length !== 1 ||
      interactions[0].code !== 'generated-command-form' ||
      interactions[0].version !== 1 ||
      commands.filter(
        (command) => commandIdentity(command) === commandIdentity(source),
      ).length !== 1
    )
      continue;
    if (decision) {
      const evidence = contracts[0].decisionEvidence;
      const accepted = requirement.settings['outcomeCodes'];
      if (
        !evidence ||
        requirement.settings['decisionKind'] !== evidence.decisionKind ||
        !Array.isArray(accepted) ||
        !accepted.length ||
        new Set(accepted).size !== accepted.length ||
        accepted.some((code) => !evidence.outcomeCodes.includes(code)) ||
        (evidence.allowOutcomeSubset !== true &&
          accepted.length !== evidence.outcomeCodes.length) ||
        source.outcomes.some((outcome) => !!outcome.destinationNodeId) ||
        (evidence.requiredCommands ?? []).some(
          (required) =>
            commands.filter((command) =>
              sameCapabilityReference(command.capability, required),
            ).length !== 1 ||
            document.capabilityReferences?.filter((reference) =>
              sameCapabilityReference(reference, required),
            ).length !== 1,
        )
      )
        continue;
    }
    const interaction = interactions[0];
    return {
      ...document,
      nodes: document.nodes.map((node) =>
        node.id !== nodeId
          ? node
          : {
              ...node,
              declaration: {
                ...declaration,
                detail: {
                  ...declaration.detail,
                  mainInteraction: {
                    code: interaction.code,
                    version: interaction.version,
                    command: structuredClone(source.capability),
                  },
                },
              },
            },
      ),
    };
  }
  return document;
}

/** Reuse the sample journey's exact contract validation; these rows are page examples, never live evidence. */
export function requirementPreviewChecklist(
  bindings: WorkflowCompletionRequirement[],
  journey: DetailSampleJourney,
): WorkflowCompletionChecklist | null {
  if (!bindings.length) return null;
  const seen = new Set<string>();
  return {
    occurrenceId: 1,
    canComplete: false,
    requirements: bindings.flatMap((binding, index) => {
      if (seen.has(binding.id)) return [];
      seen.add(binding.id);
      const sample = journey.requirements[index];
      const unavailable =
        sample?.unavailable ||
        (sample?.id === binding.id
          ? ''
          : 'The saved requirement is unavailable in this page preview.');
      return [
        {
          id: binding.id,
          provider: binding.provider,
          label: sample?.label || 'Required work',
          state: unavailable ? 'unresolved' : 'missing',
          reasonCode: 'preview.requirement',
          message: unavailable || sample!.description,
          evidence: [],
          ...(binding.provider === completionProviders.document && !unavailable
            ? {
                upload: {
                  allowedFormats: [
                    ...(binding.settings['allowedFormats'] as string[]),
                  ],
                  // Preview controls cannot upload. The live server supplies the actual size limit.
                  maximumBytes: 0,
                },
              }
            : {}),
        },
      ];
    }),
  };
}
